import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAuthContext } from "../auth/AuthContext";
import PpLoginModal from "../auth/PpLoginModal";
import { getPpToken, resolveIdentityEmail } from "../auth/ppToken";
import { adtBellErrorKey } from "../components/adtBellLogic";
import { useDarkMode } from "../theme/useDarkMode";
import { resolveWorklistIdentity } from "../worklist/resolveWorklistIdentity";
import { useWorklistAccessGate } from "../worklist/useWorklistAccessGate";
import { buildWorklistMemberNavState } from "../worklist/worklistNavState";
import {
  IpaErrorState,
  IpaForbiddenState,
  NoIpaState,
  NpiRequiredState,
  PpLoginRequiredState,
  ResolvingIpaState,
  WorklistIdentityBar,
} from "../worklist/WorklistGateStates";
import {
  PatientPriorityIndex,
  type PatientPriorityIndexSelectedMember,
} from "@astrana/ui/modules";

/**
 * Host page for @astrana/ui's native Patient Priority Index module, mounted
 * in its full-page Hospital/ED variant (`worklistVariant="hospitalEd"`) at
 * the top-level `/hospital-ed` route. Same underlying panel/dataset and
 * access gate as WorklistPage.tsx (the Patient Panel route) — this mirrors
 * that file as closely as possible; the ONLY differences are the
 * `worklistVariant` prop below and the absence of the discharge-focus
 * (ADT bell) quick-filter wiring, which is Patient-Panel-specific.
 *
 * Reuses the `PriorityWorklist` module id for gating (see HospitalEdGate in
 * routeGates.tsx) — this is the same underlying panel/dataset as the Patient
 * Panel, so a separate module id would be a new access concept nobody has
 * provisioned.
 */
export default function HospitalEdPage() {
  return <HospitalEdPageReal />;
}

function HospitalEdPageReal() {
  const navigate = useNavigate();
  const { selectedProvider, accessToken, accountLabel } = useAuthContext();
  const { isDark } = useDarkMode();

  // Same access gate as WorklistPage — see useWorklistAccessGate.ts's status
  // contract: a PP bearer (seamless PP login) AND a provider-entered/entitled
  // NPI persisted per-device.
  const accessGate = useWorklistAccessGate();

  const identityEmail = resolveIdentityEmail(accountLabel);

  // Stable ref so `getAccessToken` always reads the LATEST auth state without
  // forcing the module's `getAccessToken` prop identity (and therefore its
  // module-scoped RTK Query baseQuery closure) to be recreated on every token
  // refresh. Mirrors WorklistPage.tsx's identical pattern.
  const authRef = useRef({ selectedProvider, accessToken, identityEmail });
  authRef.current = { selectedProvider, accessToken, identityEmail };

  const getAccessToken = useCallback((): string => {
    const { selectedProvider: sp, accessToken: at, identityEmail: email } =
      authRef.current;
    // Real PP B2C bearer — NEVER a mint/exchange token (see
    // PatientPriorityIndexConfig.getAccessToken's contract).
    return getPpToken(sp, at, email) ?? "";
  }, []);

  // companyId/ipaId: the signed-in PROVIDER's own IPA/tenant scope — same as
  // WorklistPage.tsx (no member context on this route either).
  const companyId = accessGate.resolvedIpaId ?? "";

  // userId/userRole MUST be decoded off the SAME bearer `getAccessToken`
  // actually returns — see WorklistPage.tsx's note on the diverging `sub`
  // claims between the PP bearer and the primary Entra/Pathways session
  // token.
  const ppBearerToken = getPpToken(selectedProvider, accessToken, identityEmail);
  const { userId: nativeUserId, userRole: nativeUserRole } =
    resolveWorklistIdentity(ppBearerToken);

  // Row-click: navigate to the member's profile. Identical to
  // WorklistPage.tsx's handleSelectMember — see that file's doc for the full
  // `astranaMember`/`buildWorklistMemberNavState` reasoning.
  const handleSelectMember = useCallback(
    (member: PatientPriorityIndexSelectedMember) => {
      const astranaId = String(member.astranaId);
      navigate(`/members/astrana:${encodeURIComponent(astranaId)}`, {
        state: buildWorklistMemberNavState(astranaId, member),
      });
    },
    [navigate],
  );

  const handleError = useCallback((e: unknown) => {
    // Log the status only, never the raw error object — an HTTP error's
    // `data` is the response BODY and can carry PHI. Same restriction and
    // helper AdtBell.tsx's `handleError` uses (adtBellLogic.ts's
    // `adtBellErrorKey`, deliberately reads ONLY `status`).
    // eslint-disable-next-line no-console
    console.error(`[hospital-ed] Patient Priority Index error (status=${adtBellErrorKey(e)})`);
    toast.error("Something went wrong loading Hospital/ED activity.");
  }, []);

  // Gate state — identical to WorklistPage.tsx's gate rendering.
  if (accessGate.status !== "ready") {
    return (
      <div className="h-full min-h-0 flex flex-col">
        {accessGate.status === "no-pp-session" ? (
          <PpLoginRequiredState
            identityEmail={accessGate.identityEmail}
            onLogin={accessGate.requestPpLogin}
          />
        ) : accessGate.status === "resolving-ipa" ? (
          <ResolvingIpaState />
        ) : accessGate.status === "ipa-error" ? (
          <IpaErrorState onRetry={accessGate.retryIpas} />
        ) : accessGate.status === "ipa-forbidden" ? (
          <IpaForbiddenState />
        ) : accessGate.status === "no-ipa" ? (
          <NoIpaState />
        ) : (
          <NpiRequiredState error={accessGate.npiError} onSubmit={accessGate.submitNpi} />
        )}
        {accessGate.ppLoginOpen && accessGate.identityEmail && (
          <PpLoginModal
            email={accessGate.identityEmail}
            onSuccess={accessGate.handlePpLoginSuccess}
            onCancel={accessGate.cancelPpLogin}
          />
        )}
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col">
      {/* Same NPI + IPA identity bar as the Patient Panel route — see
          WorklistPage.tsx's comment on why this is always surfaced. */}
      <WorklistIdentityBar
        npi={accessGate.npi ?? ""}
        npis={accessGate.entitledNpis}
        onSelectNpi={accessGate.selectNpi}
        onChangeNpi={accessGate.clearNpi}
        ipas={accessGate.ipas}
        activeIpa={companyId}
        onSelectIpa={accessGate.selectIpa}
      />
      <PatientPriorityIndex
        ppApiBaseUrl={`${import.meta.env.VITE_PROVIDER_PORTAL_API_BASE_URL}/api`}
        companyId={companyId}
        getAccessToken={getAccessToken}
        // `status === "ready"` (guarded above) guarantees a non-null,
        // Luhn-validated 10-digit NPI — see computeWorklistAccessStatus.
        pcpNpi={accessGate.npi ?? ""}
        ipaId={companyId}
        userId={nativeUserId ?? ""}
        userRole={nativeUserRole}
        userEmail={identityEmail}
        onSelectMember={handleSelectMember}
        onError={handleError}
        theme={isDark ? "dark" : "light"}
        worklistVariant="hospitalEd"
      />
    </div>
  );
}
